/**
 * Mossy boulders: a few along the waterline (some standing in the shallows)
 * and a few at the forest edge. Pure geometry + layout; SceneShoreRocks owns
 * the meshes and the material.
 */
import * as THREE from "three";
import { mergeVertices } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import type { SiteConditions } from "../../domain/foundation/siteConditions";
import { getTerrainSlopeAtPoint } from "../../domain/foundation/siteConditions";
import { waterlineZAt } from "./lakeShore";
import {
	getGroundReliefAt,
	type WaterPlacement,
} from "./SceneEnvironment.helpers";

export const SHORE_ROCK_BUDGET = {
	shore: 11,
	forest: 7,
} as const;

function random(index: number, salt: number) {
	let h = Math.imul(index + 1, 374761393) ^ Math.imul(salt + 1, 668265263);
	h = Math.imul(h ^ (h >>> 13), 1274126177);
	return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function lattice(x: number, y: number, z: number) {
	return random(
		(Math.imul(x, 73856093) ^ Math.imul(y, 19349663) ^ Math.imul(z, 83492791)) |
			0,
		9,
	);
}

/** Smooth 3D value noise in [0, 1]. Deterministic. */
function valueNoise3(x: number, y: number, z: number) {
	const xi = Math.floor(x),
		yi = Math.floor(y),
		zi = Math.floor(z);
	const fx = x - xi,
		fy = y - yi,
		fz = z - zi;
	const u = fx * fx * (3 - 2 * fx),
		v = fy * fy * (3 - 2 * fy),
		w = fz * fz * (3 - 2 * fz);
	const lerp = THREE.MathUtils.lerp;
	const c = (dx: number, dy: number, dz: number) =>
		lattice(xi + dx, yi + dy, zi + dz);
	return lerp(
		lerp(lerp(c(0, 0, 0), c(1, 0, 0), u), lerp(c(0, 1, 0), c(1, 1, 0), u), v),
		lerp(lerp(c(0, 0, 1), c(1, 0, 1), u), lerp(c(0, 1, 1), c(1, 1, 1), u), v),
		w,
	);
}

/**
 * A lumpy, slightly flattened boulder of unit radius with its widest part at
 * y≈0: smooth normals (welded vertices), two octaves of displacement and a
 * flattened underside so it sits in the ground instead of balancing on it.
 */
export function createBoulderGeometry(seed = 0): THREE.BufferGeometry {
	const base = new THREE.IcosahedronGeometry(1, 3);
	base.deleteAttribute("normal");
	base.deleteAttribute("uv");
	const geometry = mergeVertices(base);
	base.dispose();
	const position = geometry.getAttribute("position") as THREE.BufferAttribute;
	const v = new THREE.Vector3();
	for (let i = 0; i < position.count; i += 1) {
		v.fromBufferAttribute(position, i);
		const n =
			0.62 * valueNoise3(v.x * 1.6 + seed, v.y * 1.6, v.z * 1.6) +
			0.38 * valueNoise3(v.x * 4.1, v.y * 4.1 + seed, v.z * 4.1);
		v.multiplyScalar(0.78 + 0.42 * n);
		if (v.y < -0.2) v.y = -0.2 + (v.y + 0.2) * 0.35;
		position.setXYZ(i, v.x, v.y * 0.85, v.z);
	}
	geometry.computeVertexNormals();
	geometry.computeBoundingSphere();
	return geometry;
}

export interface BoulderPlacement {
	matrix: THREE.Matrix4;
	/** Largest horizontal radius (m). */
	radius: number;
	/** "shore" boulders may stand in the shallows. */
	zone: "shore" | "forest";
}

function boulderMatrix(
	x: number,
	y: number,
	z: number,
	radius: number,
	i: number,
) {
	return new THREE.Matrix4().compose(
		new THREE.Vector3(x, y, z),
		new THREE.Quaternion().setFromEuler(
			new THREE.Euler(
				(random(i, 11) - 0.5) * 0.3,
				random(i, 12) * Math.PI * 2,
				(random(i, 13) - 0.5) * 0.3,
			),
		),
		new THREE.Vector3(
			radius,
			radius * (0.85 + random(i, 14) * 0.4),
			radius * (0.7 + random(i, 15) * 0.5),
		),
	);
}

/**
 * Deterministic boulder layout. Shore boulders straddle the waterline
 * (from ~1 m up the bank to ~2.5 m out) but keep a clear strip straight in
 * front of the sauna so the view to the water stays open. Forest boulders
 * sit in the band between the lawn and the first trees.
 */
export function layoutBoulders(
	placement: WaterPlacement,
	groundY: number,
	site: SiteConditions,
): BoulderPlacement[] {
	const out: BoulderPlacement[] = [];
	const groundAt = (x: number, z: number) =>
		groundY + getTerrainSlopeAtPoint(site, x, z) + getGroundReliefAt(x, z);

	for (let i = 0; i < SHORE_ROCK_BUDGET.shore; i += 1) {
		const side = i % 2 === 0 ? 1 : -1;
		const x = side * (2.2 + (i >> 1) * 2.1 + random(i, 1) * 1.4);
		const offset = -0.9 + random(i, 2) * 3.2; // + = into the water
		const z = waterlineZAt(x, placement) + offset;
		const radius =
			(i % 4 === 1 ? 0.5 : 0.24) + random(i, 3) * (i % 4 === 1 ? 0.4 : 0.26);
		// Rocks further out sit a little deeper in the shallows.
		const sink = offset > 0 ? 0.12 + Math.min(offset, 2) * 0.08 : 0.1;
		out.push({
			matrix: boulderMatrix(
				x,
				groundAt(x, Math.min(z, waterlineZAt(x, placement))) - radius * sink,
				z,
				radius,
				i,
			),
			radius,
			zone: "shore",
		});
	}

	for (let j = 0; j < SHORE_ROCK_BUDGET.forest; j += 1) {
		const i = j + 100;
		const angle = Math.PI + (random(i, 1) * 2 - 1) * (Math.PI / 3.2);
		const r = 12.5 + random(i, 2) * 3.5;
		const x = Math.sin(angle) * r;
		const z = Math.cos(angle) * r;
		const radius = 0.35 + random(i, 3) * 0.7;
		out.push({
			matrix: boulderMatrix(x, groundAt(x, z) - radius * 0.22, z, radius, i),
			radius,
			zone: "forest",
		});
	}
	return out;
}
